/**
 * Hook to upload market metadata to IPFS
 */

import { useMutation } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import { uploadMarketMetadata, MarketMetadata, IPFSUploadResult, IPFSProvider } from '../../utils/ipfs';

export interface UseUploadMetadataParams {
  question: string;
  description?: string;
  category: string;
  provider?: IPFSProvider;
  pinataJwt?: string; // Optional: pass JWT directly if env var not accessible
}

/**
 * Hook to upload market metadata to IPFS
 * 
 * @example
 * ```tsx
 * const { mutate: uploadMetadata, isLoading, data } = useUploadMetadata();
 * 
 * uploadMetadata({
 *   question: 'Will BTC hit $100k?',
 *   description: 'Bitcoin price prediction',
 *   category: 'crypto',
 * });
 * ```
 */
export function useUploadMetadata() {
  const { address } = useAccount();

  return useMutation({
    mutationFn: async (params: UseUploadMetadataParams): Promise<IPFSUploadResult> => {
      const metadata: MarketMetadata = {
        question: params.question,
        description: params.description,
        category: params.category,
        createdAt: Math.floor(Date.now() / 1000),
        creator: address,
      };

      return uploadMarketMetadata(metadata, params.provider, params.pinataJwt);
    },
  });
}

